import { notification } from 'antd'
import { hasValue } from '../../helpers/nullable'
import { createProposal, getCreateProposalModel, getProposalDraft, resendProposal, saveProposal, updateProposalDraft } from './proposalApi'
import { extractApiErrorMessage } from '../../helpers/apiErrorMessage'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useProposalEventsInvalidator, useProposalsInvalidator, useProposalSummaryInvalidator } from '../jobs/Proposals/proposalsQueries'
import { useWorkareaGroupsInvalidator } from '../../domain/workareas/groupsQueries'
import { ProposalCreationParameters } from './proposalsModel'

export const useCreateProposalModel = (jobId: number, modalOpen: boolean) => {
  return useQuery({
    queryKey: ['createProposal', { jobId }],
    queryFn: () => getCreateProposalModel(jobId),
    enabled: modalOpen && hasValue(jobId),
    retry: false
})
} 

export const useCreateProposalModelInvalidator = () => {
  const queryClient = useQueryClient()
  return {
    invalidate: (jobId: number) => queryClient.invalidateQueries(['createProposal', { jobId }])
  }
}

export const useProposalDraft = (proposalId: number | undefined, drawerOpen: boolean) => {
  return useQuery({
    queryKey: ['proposalDraft', { proposalId }],
    queryFn: () => getProposalDraft(proposalId!),
    enabled: drawerOpen && hasValue(proposalId),
    retry: false,
    cacheTime: 0
  })
}

export const useProposalDraftInvalidator = () => {
  const queryClient = useQueryClient()
  return {
    invalidate: (proposalId: number) => queryClient.invalidateQueries(['proposalDraft', { proposalId }])
  }
}

export const useSaveProposalMutation = () => {
  const workareaGroupsInvalidator = useWorkareaGroupsInvalidator()
  return useMutation({
    mutationFn: saveProposal,
    onSuccess: (_, data) => workareaGroupsInvalidator.invalidateForJob(data.jobId)
  })
}

export const useUpdateProposalDraftMutation = (onSuccess: () => void) => {
  const proposalsInvalidator = useProposalsInvalidator()
  const proposalSummaryInvalidator = useProposalSummaryInvalidator()
  const proposalInvalidator = useCreateProposalModelInvalidator()
  const draftInvalidator = useProposalDraftInvalidator()
  return useMutation({
    mutationFn: ({ proposalId, model }: { proposalId: number, model: ProposalCreationParameters }) =>
      updateProposalDraft(proposalId, model),
    onSuccess: (_, { proposalId, model }) => {
      proposalsInvalidator.invalidate(model.jobId)
      proposalSummaryInvalidator.invalidate(model.jobId)
      proposalInvalidator.invalidate(model.jobId)
      draftInvalidator.invalidate(proposalId)
      onSuccess()
    },
    onError: (error: unknown) => {
      notification.error({
        key: 'proposal-save-error',
        message: extractApiErrorMessage(error, 'Could not save the proposal.')
      })
    }
  })
}

export const useCreateProposalFromDrawerMutation = (onSuccess: () => void) => {
  const proposalsInvalidator = useProposalsInvalidator()
  const proposalSummaryInvalidator = useProposalSummaryInvalidator()
  const createModelInvalidator = useCreateProposalModelInvalidator()
  const workareaGroupsInvalidator = useWorkareaGroupsInvalidator()
  return useMutation({
    mutationFn: (model: ProposalCreationParameters) => createProposal(model),
    onSuccess: (_, model) => {
      proposalsInvalidator.invalidate(model.jobId)
      proposalSummaryInvalidator.invalidate(model.jobId)
      createModelInvalidator.invalidate(model.jobId)
      workareaGroupsInvalidator.invalidateForJob(model.jobId)
      onSuccess()
    },
    onError: (error: unknown) => {
      notification.error({
        key: 'proposal-create-error',
        message: extractApiErrorMessage(error, 'Could not create the proposal.')
      })
    }
  })
}

export const useResendProposalMutation = (onSuccess: () => void) => {
  const proposalsInvalidator = useProposalsInvalidator()
  const proposalEventsInvalidator = useProposalEventsInvalidator()
  return useMutation({
    mutationFn: resendProposal,
    onSuccess: (_, data) => {
      proposalsInvalidator.invalidate(data.projectId)
      proposalEventsInvalidator.invalidate(data.proposalId)
      onSuccess()
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Could not submit proposal for review.'
      notification.error({ message: errorMessage })
    }
  })
}